/**
 * A single voucher, laid out the way Tally shows it: the stock lines with
 * quantity, rate and amount first, then the accounting entries beneath.
 *
 * The inventory lines are the point of this screen. "What did we actually sell
 * and at what price" is not answerable from a register row, and every other
 * screen only ever shows a voucher's total.
 */
import { useMemo } from "react";

import { Card, Empty, Money, Scroller } from "../components/ui.js";
import { formatDate, formatMoney, formatQty } from "../derive/reports.js";
import { navigate } from "../routes.js";
import { useApp } from "../state.js";
import type { Voucher } from "@tally/shared";

export function VoucherDetail({ id }: { id: string }) {
  const { snapshot } = useApp();

  const voucher = useMemo<Voucher | null>(
    () => snapshot?.vouchers.find((v) => v.id === id) ?? null,
    [snapshot, id],
  );

  if (!voucher) return <Empty>That voucher is not in this snapshot.</Empty>;

  // Debits and credits cancel by construction, so one side is the voucher's
  // magnitude. Using the sum of both would always show zero.
  const total = voucher.ledgerEntries
    .filter((e) => e.amount < 0)
    .reduce((sum, e) => sum - e.amount, 0);

  const itemsTotal = voucher.inventoryEntries.reduce((sum, e) => sum + e.amount, 0);
  const bills = voucher.ledgerEntries.flatMap((e) =>
    e.billAllocations.map((b) => ({ ...b, ledger: e.ledger })),
  );
  const costs = voucher.ledgerEntries.flatMap((e) =>
    e.costAllocations.map((c) => ({ ...c, ledger: e.ledger })),
  );

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold">
              {voucher.voucherType} {voucher.voucherNumber}
            </div>
            {voucher.partyLedger && (
              <button
                type="button"
                className="mt-0.5 truncate text-sm text-accent"
                onClick={() => navigate({ id: "ledger", param: voucher.partyLedger! })}
              >
                {voucher.partyLedger} →
              </button>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div className="text-sm text-ink-2">{formatDate(voucher.date)}</div>
            <div className="text-lg font-semibold tabular">{formatMoney(total)}</div>
          </div>
        </div>

        {(voucher.isCancelled || voucher.isOptional) && (
          <div className="mt-3 rounded-lg bg-bad-wash p-2 text-xs text-bad">
            {voucher.isCancelled ? "This voucher is CANCELLED" : "This voucher is OPTIONAL"} —
            it is excluded from every balance and total in this app, exactly as Tally excludes
            it from the books.
          </div>
        )}

        {(voucher.reference || voucher.narration) && (
          <dl className="mt-3 space-y-1 border-t border-line pt-3 text-sm">
            {voucher.reference && (
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-ink-3">Ref</dt>
                <dd className="text-ink-2">
                  {voucher.reference}
                  {voucher.referenceDate && ` · ${formatDate(voucher.referenceDate)}`}
                </dd>
              </div>
            )}
            {voucher.narration && (
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-ink-3">Narration</dt>
                <dd className="text-ink-2">{voucher.narration}</dd>
              </div>
            )}
          </dl>
        )}
      </Card>

      {voucher.inventoryEntries.length > 0 && (
        <Card title={`Items (${voucher.inventoryEntries.length})`}>
          <Scroller>
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                  <th className="py-2 pr-3 font-medium">Item</th>
                  <th className="py-2 pr-3 text-right font-medium">Qty</th>
                  <th className="py-2 pr-3 text-right font-medium">Rate</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {voucher.inventoryEntries.map((entry, i) => (
                  <tr key={`${entry.stockItem}-${i}`}>
                    <td className="py-2 pr-3">
                      <div className="text-ink">{entry.stockItem}</div>
                      {(entry.godown || entry.batch) && (
                        <div className="text-xs text-ink-3">
                          {[entry.godown, entry.batch].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular text-ink-2">
                      {formatQty(entry.qty, entry.unit)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular text-ink-2">
                      {entry.rate ? formatMoney(entry.rate) : "—"}
                    </td>
                    <td className="py-2 text-right tabular">{formatMoney(entry.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line font-medium">
                  <td className="py-2 pr-3" colSpan={3}>
                    Goods value
                  </td>
                  <td className="py-2 text-right tabular">{formatMoney(itemsTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </Scroller>

          {Math.abs(itemsTotal - total) >= 0.01 && (
            // Normal on a GST invoice: tax and charges are ledger entries, not
            // stock lines. Showing the gap beats leaving the reader to wonder
            // why the item total is not the invoice total.
            <p className="mt-3 text-xs text-ink-3">
              Goods {formatMoney(itemsTotal)} + other charges{" "}
              {formatMoney(total - itemsTotal)} = {formatMoney(total)}. Tax, freight and
              discounts appear below as ledger entries.
            </p>
          )}
        </Card>
      )}

      <Card title="Accounting">
        <Scroller>
          <table className="w-full min-w-[26rem] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                <th className="py-2 pr-3 font-medium">Particulars</th>
                <th className="py-2 pr-3 text-right font-medium">Debit</th>
                <th className="py-2 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {voucher.ledgerEntries.map((entry, i) => (
                <tr key={`${entry.ledger}-${i}`}>
                  <td className="py-2 pr-3">
                    <button
                      type="button"
                      className="text-left text-ink hover:text-accent"
                      onClick={() => navigate({ id: "ledger", param: entry.ledger })}
                    >
                      {entry.ledger || "(unnamed)"}
                    </button>
                    {entry.bankAllocation?.instrumentNumber && (
                      <div className="text-xs text-ink-3">
                        {entry.bankAllocation.transactionType} ·{" "}
                        {entry.bankAllocation.instrumentNumber}
                        {!entry.bankAllocation.bankDate && " · unreconciled"}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular">
                    {entry.amount < 0 ? formatMoney(-entry.amount) : ""}
                  </td>
                  <td className="py-2 text-right tabular">
                    {entry.amount > 0 ? formatMoney(entry.amount) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line font-medium">
                <td className="py-2 pr-3">Total</td>
                <td className="py-2 pr-3 text-right tabular">{formatMoney(total)}</td>
                <td className="py-2 text-right tabular">{formatMoney(total)}</td>
              </tr>
            </tfoot>
          </table>
        </Scroller>
      </Card>

      {bills.length > 0 && (
        <Card title="Bill references">
          <ul className="divide-y divide-line">
            {bills.map((bill, i) => (
              <li key={`${bill.billName}-${i}`} className="flex items-baseline gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {bill.billName}
                  <span className="ml-2 text-xs text-ink-3">{bill.billType}</span>
                </span>
                {bill.dueDate && (
                  <span className="shrink-0 text-xs text-ink-3">
                    due {formatDate(bill.dueDate)}
                  </span>
                )}
                <span className="shrink-0 tabular">
                  <Money amount={bill.amount} />
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {costs.length > 0 && (
        <Card title="Cost centres">
          <ul className="divide-y divide-line">
            {costs.map((cost, i) => (
              <li key={`${cost.costCentre}-${i}`} className="flex items-baseline gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {cost.costCentre}
                  {cost.category && <span className="ml-2 text-xs text-ink-3">{cost.category}</span>}
                </span>
                <span className="shrink-0 tabular">
                  <Money amount={cost.amount} />
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
